import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './GeradorPerfis.css';
import axios from 'axios';
import DataTable, { ActionMenu } from './DataTable';
import { Icons } from './ActionIcons';
import AppModal from './AppModal';

type ClienteRow = { _id?: string; clientId: string; clientName: string; profileModel: string; status?: string; createdAt?: string };

const MODELS = ['Atividade Simples', 'Posição/Cargo Simples', 'Posição Composto + Atividade Simples'];

// module-level API base so hooks don't need it as a dependency
const URL_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const GeradorPerfis: React.FC = () => {
  // theme handled globally in AppLayout

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [rows, setRows] = useState<ClienteRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [total, setTotal] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [showModal, setShowModal] = useState(false);
  // IA parametrization for GeradorPerfis Wizard (separate from benchmark params)
  const [iaParamsOpen, setIaParamsOpen] = useState(false);
  const [iaModel, setIaModel] = useState<string>(localStorage.getItem('gpw_model') || 'gpt-4o-mini');
  const [iaBatchSize, setIaBatchSize] = useState<number>(Number(localStorage.getItem('gpw_batchSize') || '4'));
  const [iaTemperature, setIaTemperature] = useState<number>(Number(localStorage.getItem('gpw_temperature') || '0.2'));
  const [iaPrompt, setIaPrompt] = useState<string>(localStorage.getItem('gpw_prompt') || `Você é um consultor SAP Sênior e precisa classificar transações/APPs customizados em uma estrutura de processo, subprocesso e atividades.

## Definições Precisas
**Processo (macro)**: Domínio de negócio de alto nível (ex.: Financeiro, Contábil, Compras, Vendas, Produção, TI).

**Subprocesso**: Etapa/área funcional dentro do processo com objetivo claro (ex.: Compras → Requisição, Cotação, Pedido, Recebimento, Devolução, Cadastro de Fornecedor, Aprovação).

**Atividade**: Unidade mínima, coesa e orientada à finalidade de negócio, que poderá se tornar um perfil de acesso.

## Taxonomia Exemplo (Processo → exemplos de Subprocesso)
Financeiro: Contas a Pagar, Contas a Receber, Tesouraria, Gestão Fiscal, Controladoria, Fechamento Financeiro
Contábil: Lançamentos, Conciliações, Fechamento Contábil, Relatórios Contábeis
Compras: Requisição, Cotação, Pedido, Recebimento, Devolução, Cadastro de Fornecedores, Aprovações
Vendas: Cadastro de Clientes, Pedido, Expedição, Faturamento, Devolução, Aprovações
Gestão de Materiais: Administração de Estoques, Transferências, Inventário, Movimentações, Armazenagem
Produção: Planejamento de Produção, Ordens de Produção, Apontamentos, Controle de Qualidade em Produção, Manutenção da Produção
Gestão da Qualidade: Inspeção de Recebimento, Inspeção em Processo, Inspeção de Produto Final, Tratamento de Não Conformidades, Certificação
Gestão de Manutenção: Planejamento Preventivo, Execução Corretiva, Ordens de Manutenção, Gestão de Ativos, Inspeções
Gestão de Projetos: Estrutura Analítica, Planejamento de Custos, Controle Orçamentário, Acompanhamento de Atividades, Alocação de Recursos
Controle de Custos: Centros de Custo, Centros de Lucro, Rateio, Margem de Contribuição, Planejamento Orçamentário
Gestão Tributária: Apuração de Impostos, Obrigações Acessórias, Retenções, Escrituração Fiscal, Emissão de Notas, Relatórios Fiscais
Recursos Humanos: Administração de Pessoal, Folha de Pagamento, Recrutamento, Treinamento, Benefícios, Avaliação de Desempenho
Gestão Comercial: Cadastro de Produtos, Precificação, Promoções, Reposição de Estoques, Relatórios Comerciais
Gestão de Serviços: Chamados de Serviço, Ordens de Serviço, Programação de Equipes, Controle de Atendimento, Relatórios de Serviços
Gestão de Suprimentos: Planejamento de Suprimentos, Administração de Fornecedores, Avaliação de Fornecedores, Compras Estratégicas
Planejamento Empresarial: Previsão de Demanda, Planejamento de Vendas e Operações, Planejamento de Distribuição, Planejamento de Produção
Gestão de Ativos: Registro de Ativos, Depreciação, Inventário Patrimonial, Alienação de Ativos, Relatórios de Ativos
Tecnologia da Informação: Gestão de Acessos, Administração de Sistema, Monitoramento, Auditoria, Transportes, Integrações

## Segregação por camadas (OBRIGATÓRIA)
- Se a transação ou fiori app permite criar/alterar/excluir um mesmo objeto, classifique na mesma atividade nomeada como "Administrar <Objeto>" (ex.: "Administrar Pedido de Compra").
- Se a transação ou fiori app é somente consulta/relatório, classifique como atividade separada nomeada "Visualizar <Objeto>" (ex.: "Visualizar Pedido de Compra").
- Se a transação ou fiori app for de aprovação/liberação, classifique como atividade separada nomeada "Aprovar <Objeto>".
- NUNCA misture "Administrar" com "Visualizar" na mesma atividade.
- Considere o nome da transação para se orientar também a finalidade (CO, MM, SD etc.) e auxiliar na definição do processo que pertence.

**Quando incerto**: use rótulos "Não classificado" no processo, subprocesso e atividade e traga justificativa detalhada do motivo.

RESPONDA SOMENTE O JSON ARRAY COM OS CAMPOS code, process, subprocess, activity, purpose e justification!!

Exemplo de retorno:

  "items": [
    {
      "code": "Z040",
      "process": "Tecnologia da Informação",
      "subprocess": "Administração de Sistema",
      "activity": "Administrar Implementação de BAdI",
      "purpose": "Permite a implementação do Business Add-In (BAdI) /ACCGO/BADI_CAK_PP, utilizado para personalizar documentos contábeis no módulo de Gestão de Caixa (Cash Management) do SAP.",
      "justification": "Esta transação é técnica e específica para a implementação de um BAdI."
    }...`);
  // additional benchmark-like params
  const [iaTopP, setIaTopP] = useState<number>(Number(localStorage.getItem('gpw_topP') || '1'));
  const [iaMaxTokens, setIaMaxTokens] = useState<number>(Number(localStorage.getItem('gpw_maxTokens') || '4000'));
  const [iaPresencePenalty, setIaPresencePenalty] = useState<number>(Number(localStorage.getItem('gpw_presencePenalty') || '0'));
  const [iaFrequencyPenalty, setIaFrequencyPenalty] = useState<number>(Number(localStorage.getItem('gpw_frequencyPenalty') || '0.2'));
  const [iaUseWebSearch, setIaUseWebSearch] = useState<boolean>(localStorage.getItem('gpw_useWebSearch') !== 'false');
  const [iaContextMode, setIaContextMode] = useState<boolean>(localStorage.getItem('gpw_contextMode') === 'true');
  const [availableModels, setAvailableModels] = useState<string[] | null>(null);
  // removed context menu state in favor of per-row action buttons
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [form, setForm] = useState({ clientId: '', clientName: '', profileModel: MODELS[0] });

  // use module-level URL_BASE

  const fetchRows = async (p = 1, ps = pageSize) => {
    setLoading(true);
    try {
      const params: any = { page: p, pageSize: ps };
      const res = await axios.get(`${URL_BASE}/api/profilegen-clients`, { params, withCredentials: true });
      if (res && res.data) {
        setRows(Array.isArray(res.data.items) ? res.data.items : []);
        setTotal(typeof res.data.total === 'number' ? res.data.total : (Array.isArray(res.data.items) ? res.data.items.length : 0));
        setPage(p);
        setPageSize(ps);
      } else {
        setRows([]);
        setTotal(0);
      }
    } catch (err) {
      console.error('Erro fetch profilegen clients', err);
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // initial load (inline to avoid eslint hook dependency warning)
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${URL_BASE}/api/profilegen-clients`, { params: { page: 1, pageSize }, withCredentials: true });
        if (res && res.data) {
          setRows(Array.isArray(res.data.items) ? res.data.items : []);
          setTotal(typeof res.data.total === 'number' ? res.data.total : (Array.isArray(res.data.items) ? res.data.items.length : 0));
        } else { setRows([]); setTotal(0); }
      } catch (err) {
        console.error('Erro fetch profilegen clients', err);
        setRows([]);
      } finally { setLoading(false); }
    })();

    // fetch global IA params for gerador_perfis
    (async () => {
      try {
        const res = await axios.get(`${URL_BASE}/api/ia-params/gerador_perfis`, { withCredentials: true });
        if (res && res.data && res.data.ok && res.data.data) {
          const d = res.data.data;
          if (d.model) setIaModel(d.model);
          if (typeof d.batchSize !== 'undefined') setIaBatchSize(Number(d.batchSize));
          if (typeof d.temperature !== 'undefined') setIaTemperature(Number(d.temperature));
          if (d.prompt) setIaPrompt(d.prompt);
          if (typeof d.topP !== 'undefined') setIaTopP(Number(d.topP));
          if (typeof d.maxTokens !== 'undefined') setIaMaxTokens(Number(d.maxTokens));
          if (typeof d.presencePenalty !== 'undefined') setIaPresencePenalty(Number(d.presencePenalty));
          if (typeof d.frequencyPenalty !== 'undefined') setIaFrequencyPenalty(Number(d.frequencyPenalty));
          if (typeof d.useWebSearch !== 'undefined') setIaUseWebSearch(Boolean(d.useWebSearch));
          if (typeof d.contextMode !== 'undefined') setIaContextMode(Boolean(d.contextMode));
          // update localStorage as well for backward compatibility
          try {
            if (typeof d.model !== 'undefined') localStorage.setItem('gpw_model', d.model);
            if (typeof d.batchSize !== 'undefined') localStorage.setItem('gpw_batchSize', String(d.batchSize));
            if (typeof d.temperature !== 'undefined') localStorage.setItem('gpw_temperature', String(d.temperature));
            if (typeof d.prompt !== 'undefined') localStorage.setItem('gpw_prompt', d.prompt);
            if (typeof d.topP !== 'undefined') localStorage.setItem('gpw_topP', String(d.topP));
            if (typeof d.maxTokens !== 'undefined') localStorage.setItem('gpw_maxTokens', String(d.maxTokens));
            if (typeof d.presencePenalty !== 'undefined') localStorage.setItem('gpw_presencePenalty', String(d.presencePenalty));
            if (typeof d.frequencyPenalty !== 'undefined') localStorage.setItem('gpw_frequencyPenalty', String(d.frequencyPenalty));
            if (typeof d.useWebSearch !== 'undefined') localStorage.setItem('gpw_useWebSearch', String(d.useWebSearch));
            if (typeof d.contextMode !== 'undefined') localStorage.setItem('gpw_contextMode', String(d.contextMode));
          } catch (e) { /* ignore */ }
        }
      } catch (e) {
        console.debug('No global IA params or error fetching them', e);
      }
    })();
  }, [pageSize]);

  // fetch available models when modal opens
  useEffect(() => {
    if (!iaParamsOpen) return;
    const fetchModels = async () => {
      try {
        const base = process.env.REACT_APP_API_URL || '';
        const url = base ? `${base.replace(/\/$/, '')}/api/ai/models` : '/api/ai/models';
        const res = await axios.get(url, { withCredentials: true });
        if (res && res.data && res.data.ok && Array.isArray(res.data.models)) setAvailableModels(res.data.models);
      } catch (e) {
        console.warn('Não foi possível obter lista dinâmica de modelos', e);
        setAvailableModels(null);
      }
    };
    fetchModels();
  }, [iaParamsOpen]);

  const openModal = () => { setForm({ clientId: '', clientName: '', profileModel: MODELS[0] }); setShowModal(true); };
  const closeModal = () => setShowModal(false);

  const openDeleteConfirm = (id?: string) => {
    setDeleteTargetId(id || null);
    // show a modal reusing showModal? create a separate flag
    setShowDeleteConfirm(true);
  };
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editMode, setEditMode] = useState<{ enabled: boolean; id?: string }>({ enabled: false });
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [showModelChangeConfirm, setShowModelChangeConfirm] = useState(false);
  const [pendingModelChange, setPendingModelChange] = useState<{ id?: string; newModel?: string } | null>(null);

  // context menu removed; using action buttons in table rows instead

  const handleDeleteConfirmed = async () => {
    if (!deleteTargetId) return;
    try {
      setLoading(true);
      await axios.delete(`${URL_BASE}/api/profilegen-clients/${deleteTargetId}`, { withCredentials: true });
      setShowDeleteConfirm(false);
      setDeleteTargetId(null);
      fetchRows();
    } catch (err) {
      console.error('Erro deleting client', err);
      alert('Erro ao excluir: ' + (err as any)?.message || String(err));
    } finally { setLoading(false); }
  };

  const submitNew = async () => {
    if (!form.clientId || !form.clientName) return alert('Client ID e Nome são obrigatórios');
    try {
      setLoading(true);
      if (editMode.enabled && editMode.id) {
        // detect profileModel change and ask confirmation
        const original = rows.find(x => (x._id || x.clientId) === editMode.id);
        const origModel = original?.profileModel;
        if (origModel && origModel !== form.profileModel) {
          // ask confirmation: changing profile model will reset all loaded data for this client
          setPendingModelChange({ id: editMode.id, newModel: form.profileModel });
          setShowModelChangeConfirm(true);
          return;
        }
        const res = await axios.put(`${URL_BASE}/api/profilegen-clients/${editMode.id}`, { clientName: form.clientName, profileModel: form.profileModel }, { withCredentials: true });
        if (res && res.data) {
          fetchRows();
          closeModal();
          setEditMode({ enabled: false });
        }
      } else {
        try {
          const res = await axios.post(`${URL_BASE}/api/profilegen-clients`, form, { withCredentials: true });
          if (res && res.data) {
            fetchRows();
            closeModal();
          }
        } catch (err: any) {
          // handle duplicate key (409) specifically and show modal
          const status = err?.response?.status;
          if (status === 409) {
            setDuplicateMessage(err?.response?.data?.message || 'Cliente já existe');
            setShowDuplicateModal(true);
          } else {
            throw err;
          }
        }
      }
    } catch (err: any) {
      console.error('Erro criar cliente', err);
      alert('Erro ao criar cliente: ' + (err?.response?.data?.message || err.message || String(err)));
    } finally { setLoading(false); }
  };

  // when user confirms model change, perform update, reset phase and clear related tables
  const handleConfirmModelChange = async (confirm: boolean) => {
    setShowModelChangeConfirm(false);
    if (!confirm || !pendingModelChange || !pendingModelChange.id) { setPendingModelChange(null); return; }
    try {
      setLoading(true);
      // update client model and reset phase to Inicial
      const id = pendingModelChange.id;
      const resPut = await axios.put(`${URL_BASE}/api/profilegen-clients/${id}`, { profileModel: pendingModelChange.newModel, phase: 'Inicial' }, { withCredentials: true });
      const updatedClient = resPut && resPut.data ? resPut.data : null;
      const cid = updatedClient ? updatedClient.clientId : null;
      if (cid) {
        const tablesToClear = ['logs','userxa','userxpos','codez'];
        for (const t of tablesToClear) {
          try {
            await axios.delete(`${URL_BASE}/api/profilegen-data/${t}/${encodeURIComponent(cid)}`, { withCredentials: true });
          } catch (e) {
            console.debug('Error clearing table', t, e);
          }
        }
      }
      // refresh list and close modal
      fetchRows();
      closeModal();
      setEditMode({ enabled: false });
    } catch (err) {
      console.error('handleConfirmModelChange', err);
      alert('Erro ao alterar modelo e limpar dados: ' + String(err));
    } finally { setLoading(false); setPendingModelChange(null); }
  };

  const openEdit = (id?: string) => {
    const r = rows.find(x => (x._id || x.clientId) === id);
    if (!r) return alert('Registro não encontrado');
    setForm({ clientId: r.clientId, clientName: r.clientName, profileModel: r.profileModel });
    setEditMode({ enabled: true, id: id });
    setShowModal(true);
  };

  // Avatar helper functions
  const getInitials = (clientId: string | undefined): string => {
    if (!clientId) return '?';
    const text = clientId.trim().toUpperCase();
    if (text.length === 0) return '?';
    if (text.length === 1) return text;
    return text.substring(0, 2);
  };

  const getAvatarColor = (clientId: string | undefined): string => {
    if (!clientId) return '#6c757d';
    const colors = [
      '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545',
      '#fd7e14', '#ffc107', '#198754', '#20c997', '#0dcaf0'
    ];
    const hash = clientId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <>
      <div className="container py-5" style={{ marginTop: 80 }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h1 className="mb-0">Gerador de Perfis</h1>
            <p className="lead mb-0">Estruture perfis de acesso otimizados com base no consumo do cliente e no benchmark SAP.</p>
          </div>
          <div>
            <button className="btn btn-outline-secondary me-2" onClick={() => setIaParamsOpen(true)}>Parametrização IA</button>
            <button className="btn btn-secondary gp-uniform-btn" onClick={openModal}>Novo Cliente</button>
          </div>
        </div>

        <DataTable
            columns={[
              { 
                key: 'clientId', 
                label: 'Cliente (ID)', 
                sortable: true,
                render: (value: any, row: ClienteRow) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div 
                      style={{
                        width: '2rem',
                        height: '2rem',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        flexShrink: 0,
                        letterSpacing: '0.5px',
                        backgroundColor: getAvatarColor(value)
                      }}
                      title={value || ''}
                    >
                      {getInitials(value)}
                    </div>
                    <span>{value || ''}</span>
                  </div>
                )
              },
              { key: 'clientName', label: 'Nome do cliente', sortable: true },
              { key: 'profileModel', label: 'Modelo de perfis', sortable: true },
              { 
                key: 'phase', 
                label: 'Fase', 
                sortable: true,
                render: (value: any, row: ClienteRow) => (row as any).phase || 'Inicial'
              },
              { 
                key: 'createdBy', 
                label: 'Criado por', 
                sortable: true,
                render: (value: any, row: ClienteRow) => {
                  const createdBy = (row as any).createdBy;
                  return createdBy ? (createdBy.fullName || createdBy.username) : '-';
                }
              },
              {
                key: 'createdAt',
                label: 'Criado em',
                sortable: true,
                render: (value: any) => {
                  if (!value) return '-';
                  const dt = new Date(value);
                  if (Number.isNaN(dt.getTime())) return '-';
                  return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
                }
              },
              {
                key: 'actions',
                label: 'Ações',
                minWidth: 100,
                sortable: false,
                render: (value: any, row: ClienteRow) => (
                  <ActionMenu
                    actions={[
                      {
                        key: 'edit',
                        label: 'Editar',
                        icon: Icons.Pencil,
                        onClick: () => openEdit(row._id || row.clientId)
                      },
                      {
                        key: 'wizard',
                        label: 'Wizard',
                        icon: Icons.ArrowRight,
                        onClick: () => navigate(`/gerador-perfis/${encodeURIComponent(row.clientId)}`)
                      },
                      {
                        key: 'delete',
                        label: 'Excluir',
                        icon: Icons.Trash,
                        onClick: () => openDeleteConfirm(row._id || row.clientId),
                        variant: 'danger'
                      }
                    ]}
                  />
                )
              }
            ]}
            data={(() => {
              // Apply filters
              let filtered = rows.filter(row => {
                return Object.entries(columnFilters).every(([key, value]) => {
                  if (!value) return true;
                  const cellValue = String(
                    key === 'phase' ? ((row as any).phase || 'Inicial') :
                    key === 'createdBy' ? ((row as any).createdBy?.fullName || (row as any).createdBy?.username || '-') :
                    (row as any)[key] || ''
                  ).toLowerCase();
                  return cellValue.includes(value.toLowerCase());
                });
              });
              
              // Apply sorting
              if (sortColumn) {
                filtered = [...filtered].sort((a, b) => {
                  if (sortColumn === 'createdAt') {
                    const aTime = (a as any).createdAt ? new Date((a as any).createdAt).getTime() : 0;
                    const bTime = (b as any).createdAt ? new Date((b as any).createdAt).getTime() : 0;
                    const dateComparison = aTime - bTime;
                    return sortDirection === 'asc' ? dateComparison : -dateComparison;
                  }

                  let aVal = sortColumn === 'phase' ? ((a as any).phase || 'Inicial') :
                             sortColumn === 'createdBy' ? ((a as any).createdBy?.fullName || (a as any).createdBy?.username || '-') :
                             (a as any)[sortColumn] || '';
                  let bVal = sortColumn === 'phase' ? ((b as any).phase || 'Inicial') :
                             sortColumn === 'createdBy' ? ((b as any).createdBy?.fullName || (b as any).createdBy?.username || '-') :
                             (b as any)[sortColumn] || '';
                  const comparison = String(aVal).localeCompare(String(bVal));
                  return sortDirection === 'asc' ? comparison : -comparison;
                });
              }
              
              return filtered;
            })()}
            keyExtractor={(row: ClienteRow) => row._id || row.clientId}
            loading={loading}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={(column: string, direction?: 'asc' | 'desc') => {
              if (direction) {
                setSortColumn(column);
                setSortDirection(direction);
              } else if (sortColumn === column) {
                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
              } else {
                setSortColumn(column);
                setSortDirection('asc');
              }
            }}
            filters={columnFilters}
            onFilter={(columnKey: string, value: string) => {
              setColumnFilters(prev => ({ ...prev, [columnKey]: value }));
            }}
            striped
            hover
            responsive
            emptyMessage="Nenhum cliente cadastrado."
            pagination={true}
            currentPage={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={(newPage: number) => {
              setPage(newPage);
              fetchRows(newPage, pageSize);
            }}
            onPageSizeChange={(size: number) => {
              setPageSize(size);
              setPage(1);
              fetchRows(1, size);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />

        <AppModal
          show={showModal}
          title="Novo Cliente - Gerador de Perfis"
          onClose={() => { closeModal(); setEditMode({ enabled: false }); }}
          size="lg"
          footer={(
            <>
              <button className="btn btn-secondary gp-uniform-btn" onClick={() => { closeModal(); setEditMode({ enabled: false }); }}>
                Cancelar
              </button>
              <button className="btn btn-primary gp-uniform-btn" onClick={submitNew}>
                {editMode.enabled ? 'Salvar alterações' : 'Salvar'}
              </button>
            </>
          )}
        >
          <div className="mb-2">
            <label className="form-label">Cliente (ID)</label>
            <input className="form-control" value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} readOnly={editMode.enabled} />
          </div>
          <div className="mb-2">
            <label className="form-label">Nome do cliente</label>
            <input className="form-control" value={form.clientName} onChange={e => setForm({ ...form, clientName: e.target.value })} />
          </div>
          <div className="mb-2">
            <label className="form-label">Modelo de perfis</label>
            <select className="form-select" value={form.profileModel} onChange={e => setForm({ ...form, profileModel: e.target.value })}>
              {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </AppModal>

  {/* context menu removed; actions are inline in the table */}

        {/* Duplicate client modal */}
        {showDuplicateModal && (
          <div className="gp-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowDuplicateModal(false); }}>
            <div className="gp-modal card p-3" onClick={e => e.stopPropagation()}>
              <h5>Cliente já existe</h5>
              <p>{duplicateMessage}</p>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary gp-uniform-btn" onClick={() => setShowDuplicateModal(false)}>Fechar</button>
              </div>
            </div>
          </div>
        )}

        {/* Model change confirmation */}
        {showModelChangeConfirm && (
          <div className="gp-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowModelChangeConfirm(false); }}>
            <div className="gp-modal card p-3" onClick={e => e.stopPropagation()}>
              <h5>Confirmar alteração do modelo de perfis</h5>
              <p>Alterar o modelo de perfis para este cliente exigirá apagar todos os dados carregados relacionados a ele (logs, usuário×área e códigos). Deseja continuar?</p>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={() => handleConfirmModelChange(false)}>Cancelar</button>
                <button className="btn btn-danger" onClick={() => handleConfirmModelChange(true)}>Confirmar e apagar dados</button>
              </div>
            </div>
          </div>
        )}

        {/* IA Parametrização modal (for GeradorPerfis Wizard only) */}
        {iaParamsOpen && (
          <div className="users-overlay">
            <div className="users-overlay-box" style={{ width: 920, maxWidth: '95%' }} onClick={e => e.stopPropagation()}>
              <button className="modal-close-btn" aria-label="Fechar" onClick={() => setIaParamsOpen(false)}>×</button>
              <h4 style={{ marginTop: 2 }}>Parâmetros IA — Gerador de Perfis (globais)</h4>
              <p style={{ color: '#666', marginBottom: 12 }}>Configurações globais compartilhadas para classificação automática das TCODES Z. Estas substituem as configurações locais do navegador.</p>

              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: '0 0 360px' }}>
                  <div className="mb-2">
                    <label className="form-label">Modelo</label>
                    {availableModels && availableModels.length > 0 ? (
                      <select className="form-select" value={iaModel} onChange={e => setIaModel(e.target.value)}>
                        {availableModels.map(m => (<option key={m} value={m}>{m}</option>))}
                      </select>
                    ) : (
                      <input className="form-control" value={iaModel} onChange={e => setIaModel(e.target.value)} placeholder="gpt-3.5-turbo" />
                    )}
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Tamanho do lote</label>
                    <input type="number" min={1} className="form-control" value={iaBatchSize} onChange={e => setIaBatchSize(Number(e.target.value || '1'))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Temperature (0-2)</label>
                    <input type="number" step="0.01" min={0} max={2} className="form-control" value={iaTemperature} onChange={e => setIaTemperature(Number(e.target.value))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Top P (0-1)</label>
                    <input type="number" step="0.01" min={0} max={1} className="form-control" value={iaTopP} onChange={e => setIaTopP(Number(e.target.value || '1'))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Max tokens</label>
                    <input type="number" min={64} className="form-control" value={iaMaxTokens} onChange={e => setIaMaxTokens(Number(e.target.value || '1500'))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Presence penalty</label>
                    <input type="number" step="0.01" min={-2} max={2} className="form-control" value={iaPresencePenalty} onChange={e => setIaPresencePenalty(Number(e.target.value || '0'))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Frequency penalty</label>
                    <input type="number" step="0.01" min={-2} max={2} className="form-control" value={iaFrequencyPenalty} onChange={e => setIaFrequencyPenalty(Number(e.target.value || '0'))} />
                  </div>
                  <div className="mb-2 form-check">
                    <input id="gpw_contextMode" className="form-check-input" type="checkbox" checked={iaContextMode} onChange={e => setIaContextMode(e.target.checked)} />
                    <label className="form-check-label" htmlFor="gpw_contextMode">Análise em contexto (manter conversa entre lotes)</label>
                  </div>
                  <div className="mb-2 form-check">
                    <input id="gpw_useWebSearch" className="form-check-input" type="checkbox" checked={iaUseWebSearch} onChange={e => setIaUseWebSearch(e.target.checked)} />
                    <label className="form-check-label" htmlFor="gpw_useWebSearch">Habilitar Web Search (OpenAI tools)</label>
                  </div>
                </div>

                <div style={{ flex: 1 }}>
                  <label className="form-label">Prompt (OBJETIVO)</label>
                  <textarea className="form-control" rows={18} value={iaPrompt} onChange={e => setIaPrompt(e.target.value)} />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button className="btn btn-secondary" onClick={() => setIaParamsOpen(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={async () => {
                  try {
                    const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
                    const url = base + '/api/ia-params/gerador_perfis';
                    const payload = { data: { model: iaModel, batchSize: iaBatchSize, temperature: iaTemperature, prompt: iaPrompt, topP: iaTopP, maxTokens: iaMaxTokens, presencePenalty: iaPresencePenalty, frequencyPenalty: iaFrequencyPenalty, useWebSearch: iaUseWebSearch, contextMode: iaContextMode } };
                    try { await axios.post(url, payload, { withCredentials: true }); } catch (e) { console.warn('Erro salvar params no servidor', e); }
                    localStorage.setItem('gpw_model', iaModel);
                    localStorage.setItem('gpw_batchSize', String(iaBatchSize));
                    localStorage.setItem('gpw_temperature', String(iaTemperature));
                    localStorage.setItem('gpw_prompt', iaPrompt);
                    localStorage.setItem('gpw_topP', String(iaTopP));
                    localStorage.setItem('gpw_maxTokens', String(iaMaxTokens));
                    localStorage.setItem('gpw_presencePenalty', String(iaPresencePenalty));
                    localStorage.setItem('gpw_frequencyPenalty', String(iaFrequencyPenalty));
                    localStorage.setItem('gpw_useWebSearch', String(iaUseWebSearch));
                    localStorage.setItem('gpw_contextMode', String(iaContextMode));
                    try { window.dispatchEvent(new Event('gpw_params_updated')); } catch (e) { /* ignore */ }
                  } catch (e) {
                    console.error('Erro saving IA params', e);
                  }
                  setIaParamsOpen(false);
                }}>Salvar (global)</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation modal */}
        {showDeleteConfirm && (
          <div className="gp-modal-backdrop">
            <div className="gp-modal card p-3">
              <h5>Confirmar exclusão</h5>
              <p>Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.</p>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={() => { setShowDeleteConfirm(false); setDeleteTargetId(null); }}>Cancelar</button>
                <button className="btn btn-danger" onClick={handleDeleteConfirmed}>Excluir</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default GeradorPerfis;
